import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { PublicUser } from '@perplexicon/api-client';

import { Avatar } from './Avatar.js';
import { Card } from './Card.js';

export interface UserCardProps {
  user: PublicUser;
  /** Small supplementary line under the username (e.g. "Friends since …"). */
  meta?: string;
  /** Row-trailing controls (buttons), rendered outside the profile link. */
  actions?: ReactNode;
}

/**
 * A single-row summary of another user (TECH_STACK §16), linking to their
 * public profile. Used across search results, friends/requests lists,
 * blocked-users, and notification actors so every "another user" surface
 * looks and behaves the same way.
 */
export function UserCard({ user, meta, actions }: UserCardProps) {
  return (
    <Card className="interactive-lift flex flex-col items-stretch justify-between gap-3 p-3 tablet:flex-row tablet:items-center">
      <Link
        to={`/users/${encodeURIComponent(user.username)}`}
        className="flex min-w-0 items-center gap-3 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-focusRing"
      >
        <Avatar seed={user.id} label={user.username} config={user.avatar} />
        <span className="min-w-0">
          <span className="block truncate font-medium text-textPrimary">{user.username}</span>
          {meta && <span className="block truncate text-xs text-textSecondary">{meta}</span>}
        </span>
      </Link>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </Card>
  );
}
